import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Truck,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  RotateCcw,
  AlertTriangle,
  Send,
} from 'lucide-react';
import { apiGet, apiPatch, apiPost } from '../lib/api';
import { formatNTD, formatDate } from '@yiweiwu/shared';
import type { OrderDetail as OrderDetailType } from '../lib/types';
import { StatusBadge } from '../components/StatusBadge';
import { ConfirmDialog } from '../components/ConfirmDialog';

/** 操作按鈕的設定 */
const ACTION_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; variant: string; confirmMessage?: string }
> = {
  pay: {
    label: '確認付款',
    icon: <CheckCircle className="h-4 w-4" />,
    variant: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  cancel: {
    label: '取消訂單',
    icon: <XCircle className="h-4 w-4" />,
    variant: 'bg-gray-200 hover:bg-gray-300 text-gray-700',
    confirmMessage: '確定要取消此訂單嗎？此操作無法復原。',
  },
  start_prepare: {
    label: '開始備貨',
    icon: <Package className="h-4 w-4" />,
    variant: 'bg-purple-600 hover:bg-purple-700 text-white',
  },
  ship: {
    label: '確認出貨',
    icon: <Truck className="h-4 w-4" />,
    variant: 'bg-teal-600 hover:bg-teal-700 text-white',
  },
  confirm_arrival: {
    label: '確認送達',
    icon: <CheckCircle className="h-4 w-4" />,
    variant: 'bg-green-600 hover:bg-green-700 text-white',
  },
  complete: {
    label: '完成訂單',
    icon: <CheckCircle className="h-4 w-4" />,
    variant: 'bg-green-700 hover:bg-green-800 text-white',
  },
  return: {
    label: '退貨處理',
    icon: <RotateCcw className="h-4 w-4" />,
    variant: 'bg-red-600 hover:bg-red-700 text-white',
    confirmMessage: '確定要處理退貨嗎？此操作將標記訂單為已退回。',
  },
};

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  // 確認對話框
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<string>('');
  const [actionNote, setActionNote] = useState('');

  // 備註
  const [newNote, setNewNote] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);

  // 物流
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [shipLoading, setShipLoading] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const result = await apiGet<OrderDetailType>(`/orders/${id}`);
      setOrder(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '載入訂單失敗');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleAction = useCallback(
    async (action: string, withConfirmation = false) => {
      if (!order) return;

      const config = ACTION_CONFIG[action];
      const isRisky = action === 'cancel' || action === 'return';

      if (isRisky && !withConfirmation) {
        setPendingAction(action);
        setConfirmOpen(true);
        return;
      }

      try {
        setActionLoading(action);
        await apiPatch(`/orders/${order.id}/status`, {
          action,
          note: actionNote || undefined,
          requireConfirmation: isRisky ? true : undefined,
        });
        setActionNote('');
        setConfirmOpen(false);
        await fetchOrder();
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : '操作失敗');
      } finally {
        setActionLoading('');
      }
    },
    [order, actionNote, fetchOrder],
  );

  const handleAddNote = useCallback(async () => {
    if (!order || !newNote.trim()) return;
    try {
      setNoteLoading(true);
      await apiPost(`/orders/${order.id}/notes`, { note: newNote.trim() });
      setNewNote('');
      await fetchOrder();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : '新增備註失敗');
    } finally {
      setNoteLoading(false);
    }
  }, [order, newNote, fetchOrder]);

  const handleShip = useCallback(async () => {
    if (!order || !trackingNumber.trim()) return;
    try {
      setShipLoading(true);
      await apiPost(`/orders/${order.id}/ship`, {
        trackingNumber: trackingNumber.trim(),
        carrier: carrier.trim() || undefined,
      });
      setTrackingNumber('');
      setCarrier('');
      await fetchOrder();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : '物流更新失敗');
    } finally {
      setShipLoading(false);
    }
  }, [order, trackingNumber, carrier, fetchOrder]);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-brand-bake border-t-brand-gold" />
        <p className="mt-4 text-gray-400">載入訂單明細...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchOrder}
          className="px-4 py-2 rounded-lg bg-brand-gold text-white text-sm hover:bg-brand-gold/90"
        >
          重新載入
        </button>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div>
      {/* 頁首 */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/orders')}
          className="p-2 rounded-lg hover:bg-brand-bake/50 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-brand-cocoa font-heading">
              {order.orderNumber}
            </h2>
            <StatusBadge status={order.status} />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            建立於 {formatDate(order.createdAt, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左欄：訂單資訊 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
            <h3 className="text-base font-semibold text-brand-cocoa mb-4">
              訂單資訊
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">訂單狀態：</span>
                <StatusBadge status={order.status} />
              </div>
              <div>
                <span className="text-gray-500">付款狀態：</span>
                <StatusBadge status={order.paymentStatus} type="payment" />
              </div>
              <div>
                <span className="text-gray-500">付款方式：</span>
                <span className="font-medium">{order.paymentMethod ?? '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">物流方式：</span>
                <span className="font-medium">{order.shippingMethod ?? '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">物流狀態：</span>
                <span className="font-medium">{order.logisticsStatus ?? '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">優惠代碼：</span>
                <span className="font-medium">{order.couponCode ?? '-'}</span>
              </div>
            </div>
          </div>

          {/* 客戶資訊 */}
          {order.customer && (
            <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
              <h3 className="text-base font-semibold text-brand-cocoa mb-4">
                客戶資訊
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">姓名：</span>
                  <span className="font-medium">{order.customer.name}</span>
                </div>
                <div>
                  <span className="text-gray-500">電話：</span>
                  <span className="font-medium">{order.customer.phone ?? '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Email：</span>
                  <span className="font-medium">{order.customer.email ?? '-'}</span>
                </div>
              </div>
              {(order.shippingAddress || order.shippingName) && (
                <div className="mt-4 pt-4 border-t border-brand-bake/50">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">收件資訊</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">收件人：</span>
                      <span className="font-medium">{order.shippingName ?? '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">電話：</span>
                      <span className="font-medium">{order.shippingPhone ?? '-'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">地址：</span>
                      <span className="font-medium">{order.shippingAddress ?? '-'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 品項列表 */}
          <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
            <h3 className="text-base font-semibold text-brand-cocoa mb-4">
              訂購品項
            </h3>
            <div className="divide-y divide-brand-bake/50">
              {order.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {item.productNameSnapshot}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatNTD(item.priceSnapshot)} x {item.quantity}
                    </p>
                  </div>
                  <div className="ml-4 text-sm font-medium text-gray-900 flex-shrink-0">
                    {formatNTD(item.subtotal)}
                  </div>
                </div>
              ))}
            </div>

            {/* 金額摘要 */}
            <div className="mt-4 pt-4 border-t border-brand-bake space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">小計</span>
                <span>{formatNTD(order.subtotal)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-status-error">
                  <span>折扣</span>
                  <span>-{formatNTD(order.discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">運費</span>
                <span>{formatNTD(order.shippingFee)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-brand-bake font-bold text-base">
                <span>總計</span>
                <span className="text-brand-caramel">{formatNTD(order.total)}</span>
              </div>
              {order.pointsUsed > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>使用點數</span>
                  <span>{order.pointsUsed} 點</span>
                </div>
              )}
              {order.pointsEarned > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>獲得點數</span>
                  <span>{order.pointsEarned} 點</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 右欄：狀態操作與時間軸 */}
        <div className="space-y-6">
          {/* 操作按鈕 */}
          {order.allowedActions.length > 0 && (
            <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
              <h3 className="text-base font-semibold text-brand-cocoa mb-4">
                可用操作
              </h3>
              <div className="space-y-2">
                {order.allowedActions.map((action) => {
                  const config = ACTION_CONFIG[action];
                  if (!config) return null;
                  return (
                    <button
                      key={action}
                      onClick={() => handleAction(action)}
                      disabled={actionLoading === action}
                      className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${config.variant}`}
                    >
                      {config.icon}
                      {actionLoading === action ? '處理中...' : config.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* 物流更新 */}
          {order.status === 'preparing' && (
            <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
              <h3 className="text-base font-semibold text-brand-cocoa mb-4">
                物流資訊
              </h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="物流單號 *"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-brand-bake bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                />
                <input
                  type="text"
                  placeholder="物流業者（選填）"
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-brand-bake bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold"
                />
                <button
                  onClick={handleShip}
                  disabled={!trackingNumber.trim() || shipLoading}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Truck className="h-4 w-4" />
                  {shipLoading ? '更新中...' : '更新物流'}
                </button>
              </div>
            </div>
          )}

          {/* 內部備註 */}
          <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
            <h3 className="text-base font-semibold text-brand-cocoa mb-4">
              內部備註
            </h3>
            <div className="space-y-3">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="輸入內部備註..."
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-brand-bake bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold resize-none"
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || noteLoading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-brand-gold hover:bg-brand-gold/90 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
                {noteLoading ? '新增中...' : '新增備註'}
              </button>
            </div>
          </div>

          {/* 狀態時間軸 */}
          <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
            <h3 className="text-base font-semibold text-brand-cocoa mb-4">
              狀態時間軸
            </h3>
            {order.events.length === 0 ? (
              <p className="text-sm text-gray-400">尚無狀態變更記錄</p>
            ) : (
              <div className="space-y-4">
                {order.events.map((event) => (
                  <div key={event.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="h-2 w-2 rounded-full bg-brand-gold mt-1.5" />
                      <div className="flex-1 w-px bg-brand-bake mt-1" />
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {event.eventType === 'internal_note'
                            ? '內部備註'
                            : event.eventType === 'logistics_update'
                              ? '物流更新'
                              : getEventLabel(event.eventType)}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDate(event.createdAt, {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      {event.fromStatus && event.toStatus && event.fromStatus !== event.toStatus && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          <StatusBadge status={event.fromStatus} /> →{' '}
                          <StatusBadge status={event.toStatus} />
                        </p>
                      )}
                      {event.note && (
                        <p className="mt-1 text-sm text-gray-600 bg-brand-cream p-2 rounded-lg">
                          {event.note}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 確認對話框 */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="確認操作"
        description={
          ACTION_CONFIG[pendingAction]?.confirmMessage ??
          `確定要執行此操作嗎？`
        }
        confirmLabel="確定"
        cancelLabel="取消"
        variant={pendingAction === 'return' ? 'danger' : 'warning'}
        onConfirm={() => handleAction(pendingAction, true)}
        loading={actionLoading === pendingAction}
      />
    </div>
  );
}

function getEventLabel(eventType: string): string {
  const labels: Record<string, string> = {
    pay: '付款',
    cancel: '取消',
    start_prepare: '開始備貨',
    ship: '出貨',
    confirm_arrival: '確認送達',
    complete: '完成',
    return: '退貨',
  };
  return labels[eventType] ?? eventType;
}
